"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card">
        <p className="font-default text-xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-secondary text-secondary hover:bg-primary hover:text-white">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Enter your name" className="h-12 px-0 bg-transparent border-0 border-b border-secondary font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="role" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Your Role
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Parent, Attorney, Case Manager, etc." className="h-12 px-0 bg-transparent border-0 border-b border-secondary font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Email
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="email@example.com" className="h-12 px-0 bg-transparent border-0 border-b border-secondary font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Phone
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="(555) 000-0000" className="h-12 px-0 bg-transparent border-0 border-b border-secondary font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Service Requested
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-12 px-0 bg-transparent border-0 border-b border-secondary font-default text-lg text-foreground focus:border-primary focus:ring-0 data-[placeholder]:text-muted-foreground/60 w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="border-secondary bg-popover">
                  <SelectItem value="Supervised Parenting Time" className="font-default">Supervised Parenting Time</SelectItem>
                  <SelectItem value="Monitored Exchange" className="font-default">Monitored Exchange</SelectItem>
                  <SelectItem value="Parenting Education" className="font-default">Parenting Education</SelectItem>
                  <SelectItem value="Other" className="font-default">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Additional Details
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Please provide any relevant context or court-ordered requirements." rows={5} className="px-0 py-3 bg-transparent border-0 border-b border-secondary font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem className="flex flex-row items-center gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-secondary data-[state=checked]:bg-primary data-[state=checked]:border-primary size-5" />
            </FormControl>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground cursor-pointer">
              I acknowledge this is a neutral, professional service.
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="text-destructive font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group w-full h-14 bg-primary text-white hover:bg-primary/90 font-mono text-sm uppercase tracking-widest">
        <span>{isSubmitting ? "Sending..." : "Submit Inquiry"}</span>
        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1.5" />
      </Button>

      <div className="pt-8 border-t border-secondary/30 space-y-4">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Want to hear from families and professionals we've worked with? Read our testimonials.
        </p>
        <Button asChild variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-white font-mono text-xs uppercase tracking-widest h-12 px-8">
          <Link to="/WhatFamiliesAndProfessionalsSayPage">
            Read Testimonials
          </Link>
        </Button>
      </div>

      <div className="flex justify-end">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          We prioritize neutral, timely communication.
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const infoBlocks = [{
    label: "Business/General Inquiries",
    value: "admin@tinytoteenservices.org"
  }, {
    label: "Professional Referrals",
    value: "admin@tinytoteenservices.org"
  }, {
    label: "Location",
    value: "Serving families across Arizona"
  }];
  return <section data-section-id="3822" id="reach-out" className="bg-background text-foreground pt-32 md:pt-48 lg:pt-[200px] pb-24 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block size-2 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                § Contact — Tiny to Teen Supervised Parenting & Education Services
              </span>
            </div>

            <h1 className="font-default font-normal uppercase leading-[0.95] tracking-tight text-foreground mb-10" style={{
            fontSize: "clamp(3rem, 7vw, 7rem)"
          }}>
              Start the
              <br />
              intake
              <br />
              process<span className="text-primary">.</span>
            </h1>

            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground max-w-md mb-6">
              We provide safe, neutral, and structured support for families navigating court-involved matters. Reach out to begin your intake.
            </p>

            <Button asChild variant="outline" className="mb-10 h-auto py-4 px-6 border-secondary text-secondary hover:bg-secondary hover:text-white text-left whitespace-normal max-w-md justify-start">
              <Link to="/ResourcesPage">
                Review our Service Guidelines to understand how we work, what we expect, and how we support your family.
              </Link>
            </Button>

            <p className="font-default text-base leading-relaxed text-muted-foreground/80 max-w-md mb-12">
              For general business inquiries, please use our general email. For formal case referrals from attorneys or case managers, please contact our administration directly.
            </p>

            <div className="space-y-0 border-t border-secondary">
              {infoBlocks.map((block, i) => <div key={i} data-index={i} className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-2 md:gap-6 py-6 border-b border-secondary">
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground pt-1">
                    {block.label}
                  </span>
                  <span className="font-default text-lg text-foreground">
                    {block.value}
                  </span>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-700 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4 ml-1">
              Note: This form is routed to admin@tinytoteenservices.org
            </p>
            <FormProvider formKey="reach_out_form_contact" formId="reach_out_form_contact" sectionName="reach-out" defaultValues={{
            name: "",
            email: "",
            phone: "",
            role: "",
            inquiry_type: "",
            message: "",
            consent: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Inquiry" successMessage="Inquiry received. Our team will review your request and respond shortly." errorMessage="Transmission failed. Please try again or email us directly." className="relative bg-card border border-secondary p-8 md:p-12 lg:p-16">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}